<script setup lang="ts">
import { getLicenseExposureFilterBrands, getLicenseExposureInfoList } from '~/server/detail'
import { useAppStore } from '~/stores/app'
import { buildDisclosureExchangeSlug, buildDisclosureRegulatorSlug } from '~/utils/disclosure-route'
const { t, localePath } = useAppI18n()
const CLOSE_ICON = 'https://resources1.interface003.com/web30/2026/06/24/79597aa8-cc8a-4b81-a642-c0579965981e.png'

type TraderInfo = { traderCode?: string; traderName?: string; icon?: string }
type RelatedRiskResult = {
  exposure?: { title?: string; count?: number; isShowFilter?: boolean }
  holdingBrandHistory?: { title?: string; traderInfoList?: TraderInfo[] }
  regulationDisclosure?: { title?: string; count?: number; disclosureList?: any[] }
}

const props = withDefaults(defineProps<{ projectCode?: string }>(), { projectCode: '' })
const appStore = useAppStore()
const showDialog = ref(false)
const exposure = reactive<Record<string, any>>({})
const holdingBrandHistory = reactive<Record<string, any>>({})
const regulationDisclosure = reactive<Record<string, any>>({})
const brandsdata = ref<TraderInfo[]>([])
const traderCode = ref('')
const regulationId = ref('')
const isdisclose = ref(false)
const exposureList = ref<any[]>([])
const total = ref(0)
const pageNum = ref(1)
const LanguageCode = computed(() => appStore.LanguageCode)

const getDisclosureHref = (item: Record<string, any>) => {
  const regulatorSlug = buildDisclosureRegulatorSlug(
    item.countryCode || item.CountryCode,
    item.regulatorName,
    item.countryName,
  )
  const matchedTrader =
    (Array.isArray(item.traders) ? item.traders : []).find(
      (trader: Record<string, any>) => trader?.traderCode === props.projectCode || trader?.code === props.projectCode,
    ) || (Array.isArray(item.traders) ? item.traders[0] : null)
  const exchangeSlug = buildDisclosureExchangeSlug(
    matchedTrader?.englishFullName || matchedTrader?.EnglishFullName,
    props.projectCode,
  )
  const disclosureSlug = item.alias || item.disclosureAlias

  return `/${LanguageCode.value}/disclosure/${regulatorSlug}/${exchangeSlug}/${disclosureSlug}`
}

const assignReactive = (target: Record<string, any>, source: Record<string, any> = {}) => {
  Object.keys(target).forEach((key) => delete target[key])
  Object.keys(source || {}).forEach((key) => (target[key] = source[key]))
}

const open = (result: RelatedRiskResult = {}, id = '') => {
  assignReactive(exposure, result?.exposure || {})
  assignReactive(holdingBrandHistory, result?.holdingBrandHistory || {})
  assignReactive(regulationDisclosure, result?.regulationDisclosure || {})
  regulationId.value = id
  pageNum.value = 1
  exposureList.value = []
  traderCode.value = ''
  getfilterbrands(id, () => (showDialog.value = true))
  exposureinfolist()
}
const closeAll = () => (showDialog.value = false)
const getfilterbrands = async (id: string, fn: () => void) => {
  const brands = await getLicenseExposureFilterBrands(id).catch(() => null)
  brandsdata.value = (brands as any)?.data?.items || (brands as any)?.items || []
  fn()
}
const openrisk = (val = '') => {
  pageNum.value = 1
  exposureList.value = []
  exposureinfolist(val)
}
const exposureinfolist = async (code = '') => {
  const brands = await getLicenseExposureInfoList({
    regulationId: regulationId.value,
    pageSize: 8,
    pageNum: pageNum.value,
    traderCode: code,
  }).catch(() => null)
  const data = (brands as any)?.data || brands || {}
  ;(data?.exposureList || []).forEach((item: any) => exposureList.value.push(item))
  total.value = data?.total || 0
}
const loadfn = () => {
  pageNum.value++
  exposureinfolist()
}
defineExpose({ open })
</script>

<template>
  <ClientOnly>
    <ElDialog
      v-model="showDialog"
      custom-class="reghis-popup"
      append-to-body
      header-class="hidden"
      body-class="p-[32px_60px]"
      width="800px"
      class="riskStatement-dialog-regulatory !relative !h-[fit-content] !max-h-[690px] !overflow-hidden !rounded-[28px] !p-0"
      @close="closeAll"
    >
      <button
        type="button"
        class="absolute top-[10px] right-[10px] z-[3] flex size-[32px] cursor-pointer items-center justify-center rounded-full bg-[rgba(5,12,31,0.46)] hover:bg-[rgba(5,12,31,0.62)] rtl:right-auto rtl:left-[10px]"
        @click.stop="closeAll"
      >
        <CommonImage class="size-[12px]" :src="CLOSE_ICON" alt="close" />
      </button>
      <div class="sm:h-[100%]">
        <div
          class="border-[0.5px] border-solid border-[rgba(5,21,36,0.06)] pb-[16px] text-[20px] leading-[28px] font-[600] text-[#3D3D3D]"
        >
          {{ t('84572') }}
        </div>
        <div class="px-0">
          <div
            v-if="holdingBrandHistory?.traderInfoList?.length > 0"
            class="mb-[20px] rounded-[8px] bg-[#F7F8FA] p-[16px] pb-[4px] sm:mb-[16px] sm:p-[12px] sm:pb-[0px]"
          >
            <p class="mb-[12px] flex text-[14px] leading-[20px] text-[#3D3D3D] sm:text-[13px]">
              {{ holdingBrandHistory?.title }}
            </p>
            <div v-if="holdingBrandHistory?.traderInfoList?.length > 0" class="flex flex-wrap">
              <a
                v-for="(item, index) in holdingBrandHistory?.traderInfoList"
                :key="index"
                :href="
                  localePath({
                    name: 'exchange-detail',
                    params: {
                      slug: item?.alias,
                    },
                  })
                "
                class="mr-[16px] mb-[12px] flex space-x-[8px] sm:mr-[12px] sm:space-x-[4px] rtl:mr-[0px] rtl:ml-[16px] rtl:space-x-reverse rtl:sm:ml-[12px]"
                target="_blank"
              >
                <CommonImage
                  class="h-[20px] w-[20px] object-cover sm:h-[16px] sm:w-[16px]"
                  :src="item.icon"
                  alt="risk"
                />
                <span class="text-[16px] font-medium text-[#3D3D3D] sm:text-[13px]">{{ item.traderName }}</span>
              </a>
            </div>
          </div>
          <div v-if="regulationDisclosure?.count > 0" class="mb-[20px] sm:mb-[16px]">
            <p class="mb-[16px] flex text-[16px] leading-[22px] font-medium text-[#3D3D3D]">
              {{ regulationDisclosure?.title }} {{ regulationDisclosure?.count }}
            </p>
            <div class="flex flex-col rounded-[8px] border-[0.5px] border-solid border-[#EDEDED] p-[16px]">
              <a
                v-for="(item, index) in regulationDisclosure.disclosureList"
                v-show="Number(index) < 3 || isdisclose"
                :key="index"
                :class="[
                  'mb-[20px] cursor-pointer',
                  index === regulationDisclosure.disclosureList.length - 1 ? '!mb-[0px]' : '',
                ]"
                :href="getDisclosureHref(item)"
                target="_blank"
              >
                <div class="mb-[8px] flex w-full items-center justify-between">
                  <span class="text-[14px] font-semibold text-[#9B273A]" :style="{ color: item.disclosureTypeColor }">{{
                    item.disclosureType
                  }}</span>
                  <div class="flex items-center space-x-[6px] sm:hidden rtl:space-x-reverse">
                    <CommonImage class="h-[14px] w-[22px] object-cover" :src="item.countryFlag" alt="risk" /><span
                      class="text-[14px] font-medium text-[#3D3D3D]"
                      >{{ item.countryName }}{{ item.regulatorName }}</span
                    >
                  </div>
                </div>
                <p
                  :class="[
                    'mb-[8px] text-[16px] leading-[24px] font-medium text-[#3D3D3D]',
                    ['zh-cn', 'zh'].includes(LanguageCode) ? 'line-clamp-2' : 'line-clamp-3',
                  ]"
                >
                  {{ item.content }}
                </p>
                <div class="flex items-center space-x-[4px] rtl:space-x-reverse">
                  <div
                    v-for="(items, indexs) in item?.traders"
                    :key="indexs"
                    class="flex items-center space-x-[6px] rtl:space-x-reverse"
                  >
                    <CommonImage class="h-[16px] w-[16px] object-cover" :src="items?.icon" alt="risk" /><span
                      v-if="indexs === item?.traders?.length - 1"
                      class="text-[12px] leading-[18px] text-[#4E5969]"
                      >{{ items?.traderName }}</span
                    >
                  </div>
                  <span class="text-[12px] leading-[18px] text-[#86909C]">{{ item.disclosureDate }}</span>
                </div>
              </a>
              <div
                v-if="regulationDisclosure?.count > 3"
                :class="['flex justify-center', isdisclose ? 'mt-[36px]' : 'mt-[16px]']"
              >
                <span
                  :class="isdisclose ? '-rotate-90' : 'rotate-90'"
                  class="iconfont icon-arrow-down1 flex cursor-pointer text-[14px] text-[#C9CDD4]"
                  @click="isdisclose = !isdisclose"
                ></span>
              </div>
            </div>
          </div>
          <div v-if="exposure?.count > 0" class="">
            <div class="mt-[22px] mb-[16px] flex items-center justify-between">
              <span class="text-[16px] leading-[22px] font-medium text-[#3D3D3D]"
                >{{ exposure?.title }} {{ exposure?.count }}</span
              >
              <ElSelect
                v-if="exposure?.isShowFilter"
                v-model="traderCode"
                placeholder="请选择"
                size="small"
                style="width: 100px"
                popper-class="risk-brandsdata"
                class="risk-brandsdata-select"
                @change="openrisk"
                ><ElOption
                  v-for="item in brandsdata"
                  :key="item.traderCode"
                  :label="item.traderName"
                  :value="item.traderCode || ''"
                  style="width: 100px"
                ></ElOption
              ></ElSelect>
            </div>
            <div class="h-[fit-content] max-h-[512px] overflow-y-auto">
              <div class="rounded-[8px] border-[0.5px] border-solid border-[#EDEDED] p-[16px]">
                <a
                  v-for="(item, index) in exposureList"
                  :key="index"
                  class="mb-[20px] flex w-full flex-col last:mb-[0px]"
                  :href="
                    localePath({
                      name: 'feedback-detail',
                      params: { review_id: item.exposureId },
                    })
                  "
                  target="_blank"
                >
                  <div class="mb-[8px] flex items-center space-x-[8px] rtl:space-x-reverse">
                    <CommonImage
                      class="size-[24px] overflow-hidden rounded-[12px] object-cover"
                      :src="item.userIcon"
                      alt="risk"
                    /><span class="text-[13px] text-[#3D3D3D]">{{ item.userName }}</span>
                  </div>
                  <p
                    :class="[
                      'mb-[8px] text-[16px] leading-[24px] font-medium text-[#3D3D3D]',
                      ['zh-cn', 'zh'].includes(LanguageCode) ? 'line-clamp-2' : 'line-clamp-3',
                    ]"
                  >
                    {{ item.content }}
                  </p>
                  <div class="flex items-center space-x-[6px] rtl:space-x-reverse">
                    <CommonImage class="h-[16px] w-[16px] object-cover" :src="item?.trader?.icon" alt="risk" /><span
                      class="text-[12px] text-[#4E5969]"
                      >{{ item?.trader?.traderName }}</span
                    ><span class="text-[12px] text-[#86909C]">{{ item.exposureDate }}</span>
                  </div>
                </a>
                <div v-if="total > exposureList?.length" class="mt-[16px] flex justify-center">
                  <span class="cursor-pointer text-[13px] text-[#1558BC]" @click="loadfn">Expand</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ElDialog>
  </ClientOnly>
</template>
